import { useEffect } from 'react';
import { useMatches } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { SITE_NAME } from '@/lib/route-meta.ts';
import type { LabelQueryOptions } from '@/lib/route-meta.ts';

/**
 * Keeps `document.title` in sync with the active route — mounted once at the
 * root so it spans every route (logged and public). The page label is the
 * **closest match with a `staticData` label, leaf → root** — walking up lets an
 * index route inherit its labelled layout (e.g. `/users` titles "Users" via the
 * layout label). Static labels resolve reactively via `t` (the title re-renders
 * on locale change like the breadcrumb); dynamic ones (`labelQuery`) resolve
 * from the route's loader-primed query. Format: `Page - SITE_NAME`, bare
 * `SITE_NAME` on home / unlabelled routes or while a dynamic name loads.
 * Renders nothing — `document.title` is the external system this syncs to.
 */
export function DocumentTitle() {
  const { t } = useLingui();
  const matches = useMatches();

  const labelled = [...matches]
    .reverse()
    .find(
      (match) => match.staticData.label !== undefined || match.staticData.labelQuery !== undefined,
    );
  const label = labelled?.staticData.label;
  const labelQuery = labelled?.staticData.labelQuery;

  return labelled && labelQuery ?
      // Keyed by match id so navigating between entities remounts the single
      // dynamic query instead of ever holding two.
      <DynamicTitle key={labelled.id} query={labelQuery(labelled.params)} />
    : <StaticTitle page={label ? t(label) : null} />;
}

function useSyncDocumentTitle(page: string | null) {
  const title = page ? `${page} - ${SITE_NAME}` : SITE_NAME;
  useEffect(() => {
    document.title = title;
  }, [title]);
}

function StaticTitle({ page }: { page: string | null }) {
  useSyncDocumentTitle(page);
  return null;
}

function DynamicTitle({ query }: { query: LabelQueryOptions }) {
  const { data } = useQuery(query);
  useSyncDocumentTitle(data ?? null);
  return null;
}
